import React, { useState, useEffect } from 'react';
import { GoogleOAuthProvider } from '@react-oauth/google';
import Header from './Header';
import Tabs from './Tabs';
import ResultsPanel from './ResultsPanel';
import SignInModal from './SignInModal';
import PricingModal from './PricingModal';
import PropertySelector from './PropertySelector';
import PropertyModal from './PropertyModal';
import LandingPage from './LandingPage';
import apiService from './services/api';
import './App.css';

// TEMPORARY: For testing purposes - replace with properly configured Client ID
// The current Client ID is not configured for localhost:3000
const GOOGLE_CLIENT_ID = "509369887052-97oqetun5diapge2q0klojvoih8f8162.apps.googleusercontent.com";

// NOTE: This Client ID needs to be configured in Google Cloud Console 
// to allow http://localhost:3000 as an authorized origin

function App() {
  const [overallResults, setOverallResults] = useState(null);
  const [user, setUser] = useState(null);
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [showPropertyModal, setShowPropertyModal] = useState(false);
  // FREE LAUNCH: Removed subscription state
  // const [subscription, setSubscription] = useState(null);
  const [properties, setProperties] = useState([]);
  const [currentProperty, setCurrentProperty] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showLandingPage, setShowLandingPage] = useState(true);

  // Check authentication status on app load
  useEffect(() => {
    checkAuthStatus();
  }, []);

  // FREE LAUNCH: Fetch properties when user changes (removed subscription fetch)
  useEffect(() => {
    if (user) {
      // fetchSubscriptionStatus(); // Removed for free launch
      fetchProperties();
    } else {
      // setSubscription(null); // Removed for free launch
      setProperties([]);
      setCurrentProperty(null);
    }
  }, [user]);

  const checkAuthStatus = async () => {
    try {
      // First check for mock authentication in localStorage
      const mockToken = localStorage.getItem('authToken');
      const mockUserData = localStorage.getItem('user');

      if (mockToken && mockUserData && mockToken.startsWith('test-token-')) {
        // Use mock authentication data
        const user = JSON.parse(mockUserData);
        apiService.setToken(mockToken);
        setUser(user);
        setIsLoading(false);
        return;
      }

      // Try API authentication
      const response = await apiService.checkAuthStatus();
      if (response.authenticated) {
        setUser(response.user);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // FREE LAUNCH: Removed subscription status fetching
  // const fetchSubscriptionStatus = async () => {
  //   try {
  //     const status = await apiService.getSubscriptionStatus();
  //     setSubscription(status);
  //   } catch (error) {
  //     console.error('Failed to fetch subscription status:', error);
  //     setSubscription({ plan: 'free', is_premium: false });
  //   }
  // };

  const fetchProperties = async () => {
    try {
      console.log('📋 fetchProperties called');
      const response = await apiService.getProperties();
      const propertyList = response.results || [];
      setProperties(propertyList);
      console.log('📋 Properties loaded:', propertyList.length);

      // CRITICAL FIX: Only set current property if none is selected
      // Don't override the user's selection when they switch properties
      if (propertyList.length > 0 && !currentProperty) {
        const savedPropertyId = apiService.getCurrentPropertyId();
        const savedProperty = propertyList.find(p => p.id === savedPropertyId);
        const initialProperty = savedProperty || propertyList[0];
        setCurrentProperty(initialProperty);
        console.log('📋 Initial property set to:', initialProperty.name);
        if (!savedProperty) {
          apiService.setCurrentPropertyId(propertyList[0].id);
        }
      } else {
        console.log('📋 Keeping current property:', currentProperty?.name);
      }
    } catch (error) {
      console.error('Failed to fetch properties:', error);
    }
  };

  const handleSignIn = async (userData) => {
    // Clear guest data when signing in
    localStorage.removeItem('guestTransactions');
    localStorage.removeItem('guestPurchaseTransactions');
    localStorage.removeItem('guestSaleTransactions');
    localStorage.removeItem('guestAdditionalTransactions');

    setUser(userData);
    setShowSignInModal(false);
    setOverallResults(null);
  };

  const handleSignOut = () => {
    apiService.clearToken();
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    setUser(null);
    // setSubscription(null); // Removed for free launch
    setOverallResults(null);
  };

  const showSignInModalHandler = () => {
    setShowSignInModal(true);
  };

  const closeSignInModal = () => {
    setShowSignInModal(false);
  };

  const handleUpgradeClick = () => {
    if (!user) {
      setShowSignInModal(true);
    } else {
      setShowPricingModal(true);
    }
  };

  // FREE LAUNCH: Removed upgrade functionality
  // const handleUpgradeSuccess = (newSubscription) => {
  //   setSubscription(newSubscription);
  //   console.log('Upgrade successful:', newSubscription);
  // };

  const handlePropertyChange = (property) => {
    console.log('🔄🔄🔄 APP.JS handlePropertyChange called with property:', property);
    console.log('🔄 Current currentProperty BEFORE update:', currentProperty);

    // CRITICAL FIX: Create a new object reference to ensure React detects the change
    // This forces a re-render of components using currentProperty
    const newProperty = { ...property };

    setCurrentProperty(newProperty);
    console.log('✅ setCurrentProperty called with:', newProperty);
    apiService.setCurrentPropertyId(newProperty.id);
    console.log('✅ apiService.setCurrentPropertyId called with ID:', newProperty.id);
    setOverallResults(null); // Clear results when switching properties
    console.log('✅ Results cleared for property switch');
  };

  // Debug: Track currentProperty changes
  useEffect(() => {
    console.log('🎯🎯🎯 APP.JS currentProperty STATE CHANGED TO:', currentProperty);
  }, [currentProperty]);

  const handleAddProperty = () => {
    setShowPropertyModal(true);
  };

  const handlePropertyCreated = (newProperty) => {
    setProperties(prev => [...prev, newProperty]);
    setCurrentProperty(newProperty);
    apiService.setCurrentPropertyId(newProperty.id);
    setShowPropertyModal(false);
  };

  const handleGetStarted = () => {
    setShowLandingPage(false);
  };

  const handleBackToHome = () => {
    setShowLandingPage(true);
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      {showLandingPage ? (
        <LandingPage onGetStarted={handleGetStarted} />
      ) : (
        <div className="App">
          <Header
            user={user}
            onSignIn={showSignInModalHandler}
            onSignOut={handleSignOut}
            onBackToHome={handleBackToHome}
          // subscription={subscription} // Removed for free launch
          // onUpgradeClick={handleUpgradeClick} // Removed for free launch
          />

          {!user && (
            <div style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              padding: '16px 24px',
              textAlign: 'center',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
              borderBottom: '3px solid #5a67d8'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                flexWrap: 'wrap'
              }}>
                <span style={{ fontSize: '24px' }}>⚠️</span>
                <div style={{ flex: '1', minWidth: '300px', maxWidth: '800px' }}>
                  <strong style={{ fontSize: '1.1em', display: 'block', marginBottom: '4px' }}>
                    Guest Mode: Your data will NOT be saved
                  </strong>
                  <span style={{ fontSize: '0.95em', opacity: 0.95 }}>
                    Sign in now to save your transactions, calculations, and access tax features.
                    All guest data will be lost when you close the browser or sign in.
                  </span>
                </div>
                <button
                  onClick={showSignInModalHandler}
                  style={{
                    background: 'white',
                    color: '#667eea',
                    border: 'none',
                    padding: '10px 24px',
                    borderRadius: '6px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    fontSize: '1em',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    transition: 'transform 0.2s'
                  }}
                  onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
                  onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
                >
                  Sign In Now
                </button>
              </div>
            </div>
          )}

          {/* Property Selector - Only show for logged in users */}
          {user && (
            <div style={{
              padding: '16px 24px',
              backgroundColor: '#f8f9fa',
              borderBottom: '1px solid #e5e5e5',
              display: 'flex',
              alignItems: 'center',
              gap: '16px'
            }}>
              <span style={{ fontWeight: '500', color: '#666' }}>Property:</span>
              <PropertySelector
                properties={properties}
                currentProperty={currentProperty}
                onPropertyChange={handlePropertyChange}
                onAddProperty={handleAddProperty}
              // subscription={subscription} // Removed for free launch
              // onUpgradeClick={handleUpgradeClick} // Removed for free launch
              />
              {currentProperty && (
                <span style={{ color: '#999', fontSize: '0.9rem' }}>
                  {properties.length} {properties.length === 1 ? 'property' : 'properties'}
                </span>
              )}
            </div>
          )}

          <div className="main-content">
            <Tabs
              onOverallResultsChange={setOverallResults}
              user={user}
              // subscription={subscription} // Removed for free launch
              // onUpgradeClick={handleUpgradeClick} // Removed for free launch
              currentProperty={currentProperty}
            />
            <ResultsPanel
              overallResults={overallResults}
              user={user}
            // subscription={subscription} // Removed for free launch
            />
          </div>

          {showSignInModal && (
            <SignInModal
              onClose={closeSignInModal}
              onSignInSuccess={handleSignIn}
            />
          )}

          {/* FREE LAUNCH: Removed PricingModal */}
          {/* {showPricingModal && (
            <PricingModal
              isOpen={showPricingModal}
              onClose={() => setShowPricingModal(false)}
              onUpgradeSuccess={handleUpgradeSuccess}
              user={user}
            />
          )} */}

          {showPropertyModal && (
            <PropertyModal
              isOpen={showPropertyModal}
              onClose={() => setShowPropertyModal(false)}
              onPropertyCreated={handlePropertyCreated}
            />
          )}
        </div>
      )}
    </GoogleOAuthProvider>
  );
}

export default App;

